import React, { useEffect, useMemo } from 'react';
import {
  BaseMonitor,
  DraftMonitorProfile,
  Flex,
  Grid,
  openNotificationWithIcon,
  Page,
  PageContent,
  REQUIRED_MSG,
  useAssetParams,
} from '@mss/mss-component-library';
import { MonitorSettingsPage } from '../Edit/MonitorSettings';
import { Formik, useFormikContext } from 'formik';
import { Form } from 'formik-antd/lib/form';
import { Trans } from 'react-i18next';
import { notification } from 'antd';
import { object, string } from 'yup';
import SubmitButton from '../../SubmitButton';
import Fetch from '../../util/_fetch';
import { useHistory } from 'react-router-dom';
import { initialValues } from './util';
import ProfileForm from './Form';
import Breadcrumbs from '../../Breadcrumbs';
import { useAllMonitorsQuery } from '../../../asset/services/queries';
import { findScopedMonitors } from '../utils/findScopedMonitors';

export default function Add() {
  const history = useHistory();
  const { assetType = 'field' } = useAssetParams();
  const { data: allMonitors } = useAllMonitorsQuery();
  const hasSharedMonitors = useMemo(
    () => findScopedMonitors(allMonitors ?? [], { assetType }).length,
    [allMonitors, assetType]
  );

  async function onSubmit({
    name,
    asset_type,
    monitorsToEnable = [],
  }: DraftMonitorProfile) {
    try {
      const resp = await Fetch.post('/api/automation/monitor-profile/', {
        body: JSON.stringify({ name, asset_type }),
      });
      const result = await resp.json();

      if (!resp.ok) throw new Error(result.detail);

      const monitors = monitorsToEnable.map(monitor => ({
        ...monitor,
        monitor_name: monitor.name,
        profile: result.id,
      }));

      const resps = await Promise.all(
        monitors.map(async monitor => {
          return await Fetch.post('/api/automation/monitor-profile-monitor/', {
            body: JSON.stringify(monitor),
          });
        })
      );

      if (resps.some(resp => !resp.ok)) {
        throw new Error('Failed to add monitors to profile');
      } else {
        openNotificationWithIcon(
          notification.success,
          'Success',
          'Monitor profile has been added'
        );
        history.goBack();
      }
    } catch (e: any) {
      openNotificationWithIcon(notification.error, 'Error', `${e}`);
    }
  }

  return (
    <Page
      breadCrumbs={<Breadcrumbs />}
      main={
        <PageContent title={'Add Monitor Profile'}>
          <Formik
            initialValues={initialValues(assetType, !!hasSharedMonitors)}
            onSubmit={onSubmit}
            validationSchema={object({
              name: string().required(REQUIRED_MSG),
              asset_type: string().required(REQUIRED_MSG),
            })}
          >
            <Form>
              <ProfileWizard />
            </Form>
          </Formik>
        </PageContent>
      }
    />
  );
}

function ProfileWizard() {
  const { setFieldValue, values, errors, dirty } =
    useFormikContext<DraftMonitorProfile>();
  const { assetType = 'field' } = useAssetParams();

  useEffect(() => {
    updateAssetType();
    setFieldValue('monitorsToEnable', []);
  }, [assetType, values.assetSubType]);

  function updateAssetType() {
    setFieldValue(
      'asset_type',
      `${assetType}${values.assetSubType !== '-' ? `.${values.assetSubType}` : ''}`
    );
  }

  function setMonitor(monitor: BaseMonitor) {
    if (!values.monitorsToEnable) return;
    const matchedMonitor = values.monitorsToEnable?.find(
      m => m.name === monitor.name
    );
    const monitorEnabledOrConfig = monitor.config || monitor.enabled;
    if (!monitorEnabledOrConfig) {
      setFieldValue(
        'monitorsToEnable',
        values.monitorsToEnable.filter(m => m.name !== monitor.name)
      );
    } else {
      setFieldValue(
        'monitorsToEnable',
        matchedMonitor
          ? values.monitorsToEnable.map(m =>
              m.name !== monitor.name ? m : { ...m, ...monitor }
            )
          : [...values.monitorsToEnable, monitor]
      );
    }
  }

  return (
    <div>
      <Trans parent="p" ns={['asset', 'common']}>
        Here you can create a new monitor profile. A monitor profile is a set of
        predefined monitors configurations, but can also be overridden per
        asset.
      </Trans>
      <Trans parent="p" ns={['asset', 'common']}>
        You have the option to create them per asset/application type or one of
        their subtypes.{' '}
        <i>
          If you don't select a subtype, some monitors might be <b>missing</b>{' '}
          are specific to certain subtype.
        </i>
      </Trans>
      <Grid.Row>
        <ProfileForm updateAssetType={updateAssetType} />
      </Grid.Row>
      <Trans parent={'span'} ns={['common', 'asset']} className={'font-bold'}>
        Monitor settings
      </Trans>
      <MonitorSettingsPage
        customMonitorState={{
          set: setMonitor,
          monitors: values.monitorsToEnable ?? [],
        }}
        overrideAssetParams={{
          assetType,
          assetSubType:
            values.assetSubType !== '-' ? values.assetSubType : undefined,
        }}
      />
      <Flex className="mt-4" h="end">
        <SubmitButton disabled={Object.keys(errors).length > 0 || !dirty}>
          Submit
        </SubmitButton>
      </Flex>
    </div>
  );
}
